import type { Parameter, ParameterGroup } from "../generated/api/types.gen";

export interface ProcessingState {
  isRegenerating: boolean;
  isInitialLoading: boolean;
  isExtractingParameters: boolean;
  isExpandingGroup: boolean;
  isExpandingElements: boolean;
  isResyncingParameters: boolean;
  /** A reference image is still being prepared; submitting now would send
   *  the refinement without it. */
  isAttachingImage: boolean;
}

export function getButtonLabel(p: ProcessingState): string {
  if (p.isRegenerating) return "Generating...";
  if (p.isInitialLoading) return "Loading...";
  if (p.isExtractingParameters) return "Extracting Parameters...";
  if (p.isExpandingGroup) return "Adding Parameters...";
  if (p.isExpandingElements) return "Adding Elements...";
  if (p.isResyncingParameters) return "Updating Parameters...";
  if (p.isAttachingImage) return "Preparing Image...";
  return "Refine Further";
}

export function shouldAutoExtractParameters(args: {
  originalParameterGroups: ParameterGroup[] | null | undefined;
  isInitialLoading: boolean;
  isExtractingParameters: boolean;
}): boolean {
  return !(
    args.originalParameterGroups?.length ||
    args.isInitialLoading ||
    args.isExtractingParameters
  );
}

/** True for params the post-render re-sync may update: image-extracted,
 * not asset-backed, and never explicitly set by the user. Mirrors the
 * backend's merge_auto_parameter_values protection rules. */
export function isParameterResyncEligible(
  param: Pick<
    Parameter,
    "is_prompt_param" | "asset_id" | "asset_url" | "user_set"
  >,
): boolean {
  return !(
    param.is_prompt_param ||
    param.asset_id ||
    param.asset_url ||
    param.user_set
  );
}

export const BLANK_PARAMETER_MESSAGE = "This value can't be empty.";

/** Reject blank values only when the original image had a non-empty value for
 * this parameter (auto-undetermined empties stay allowed). */
export function validateNotBlanked(
  value: string | null | undefined,
  originalValue: string | null | undefined,
): true | string {
  if (originalValue?.trim() && !(value ?? "").trim()) {
    return BLANK_PARAMETER_MESSAGE;
  }
  return true;
}

export function canTriggerRegenerate(
  p: Pick<
    ProcessingState,
    | "isRegenerating"
    | "isInitialLoading"
    | "isExtractingParameters"
    | "isResyncingParameters"
  >,
): boolean {
  return !(
    p.isRegenerating ||
    p.isInitialLoading ||
    p.isExtractingParameters ||
    p.isResyncingParameters
  );
}

/** Shared disabled logic for the refine action: requires an actual change
 *  (parameter or prompt) and no blocking work in flight. Used by both the
 *  Regenerate button and the Enter hotkey so the two can't drift apart. */
export function isRegenerateDisabled(
  processing: ProcessingState,
  hasChanges: boolean,
): boolean {
  return (
    !(hasChanges && canTriggerRegenerate(processing)) ||
    processing.isExpandingGroup ||
    processing.isExpandingElements ||
    processing.isAttachingImage
  );
}
